import React from 'react';
import { Shield, ShieldCheck, ShieldAlert, ShieldOff, Clock, AlertTriangle } from 'lucide-react';

const ActivityChart = ({ alarms }) => {
  // Group alarms by facility
  const facilityData = alarms.reduce((acc, alarm) => {
    const anlageId = alarm.anlage || 'Unbekannt';
    if (!acc[anlageId]) {
      acc[anlageId] = {
        id: anlageId,
        alarms: [],
        lastActivity: null,
        status: 'unknown',
        unacknowledged: 0
      };
    }
    acc[anlageId].alarms.push(alarm);

    // Track unacknowledged
    if (!alarm.acknowledged) {
      acc[anlageId].unacknowledged++;
    }

    return acc;
  }, {});

  // Process each facility
  const facilities = Object.values(facilityData).map(facility => {
    // Sort alarms by timestamp (newest first)
    facility.alarms.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const lastAlarm = facility.alarms[0];
    facility.lastActivity = lastAlarm?.timestamp;
    facility.lastAlarmType = lastAlarm?.alarmArt || '';

    // Determine status based on last alarm
    const alarmArt = facility.lastAlarmType.toLowerCase();
    if (alarmArt.includes('scharf') && !alarmArt.includes('unscharf')) {
      facility.status = 'armed';
    } else if (alarmArt.includes('unscharf')) {
      facility.status = 'disarmed';
    } else if (alarmArt.includes('alarm') || alarmArt.includes('einbruch') || alarmArt.includes('sabotage') || alarmArt.includes('brand')) {
      facility.status = 'alert';
    } else {
      facility.status = 'normal';
    }

    // Check for critical alarms
    facility.hasCritical = facility.alarms.some(a =>
      !a.acknowledged && (
        a.alarmArt.toLowerCase().includes('einbruch') ||
        a.alarmArt.toLowerCase().includes('sabotage') ||
        a.alarmArt.toLowerCase().includes('brand') ||
        a.alarmArt.toLowerCase().includes('überfall')
      )
    );

    return facility;
  });

  // Sort: critical first, then by last activity
  facilities.sort((a, b) => {
    if (a.hasCritical && !b.hasCritical) return -1;
    if (!a.hasCritical && b.hasCritical) return 1;
    return new Date(b.lastActivity) - new Date(a.lastActivity);
  });

  const getStatusIcon = (status, hasCritical) => {
    if (hasCritical) return <ShieldAlert className="w-5 h-5 text-red-500" />;
    switch (status) {
      case 'armed': return <ShieldCheck className="w-5 h-5 text-green-500" />;
      case 'disarmed': return <ShieldOff className="w-5 h-5 text-gray-400" />;
      case 'alert': return <ShieldAlert className="w-5 h-5 text-orange-500" />;
      default: return <Shield className="w-5 h-5 text-blue-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'armed': return 'Scharf';
      case 'disarmed': return 'Unscharf';
      case 'alert': return 'Alarm';
      default: return 'Aktiv';
    }
  };

  const getStatusBg = (status, hasCritical) => {
    if (hasCritical) return 'bg-red-500/20 border-red-500/50';
    switch (status) {
      case 'armed': return 'bg-green-500/20 border-green-500/50';
      case 'disarmed': return 'bg-gray-500/20 border-gray-500/50';
      case 'alert': return 'bg-orange-500/20 border-orange-500/50';
      default: return 'bg-blue-500/20 border-blue-500/50';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Jetzt';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  return (
    <div className="bg-[#333333] rounded-xl p-4 text-[#F2E2D2] relative overflow-hidden">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="font-medium">Anlagen-Status</h3>
          <p className="text-xs text-[#F2E2D2]/60">
            {facilities.length} Anlagen überwacht
          </p>
        </div>
        {facilities.some(f => f.hasCritical) && (
          <div className="flex items-center gap-1 px-2 py-1 bg-red-500/30 rounded-full">
            <AlertTriangle className="w-3 h-3 text-red-400" />
            <span className="text-xs text-red-400">Kritisch</span>
          </div>
        )}
      </div>

      <div className="space-y-2 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
        {facilities.length === 0 ? (
          <div className="text-center py-4 text-[#F2E2D2]/40 text-sm">
            Keine Anlagen gefunden
          </div>
        ) : (
          facilities.map(facility => (
            <div
              key={facility.id}
              className={`flex items-center justify-between p-2 rounded-lg border ${getStatusBg(facility.status, facility.hasCritical)} transition-colors`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {getStatusIcon(facility.status, facility.hasCritical)}
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">Anlage {facility.id}</div>
                  <div className="text-xs text-[#F2E2D2]/60 truncate">{facility.lastAlarmType || '-'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {facility.unacknowledged > 0 && (
                  <span className="px-2 py-0.5 bg-[#e89f71] text-white text-xs rounded-full">
                    {facility.unacknowledged}
                  </span>
                )}
                <div className="text-right">
                  <div className="text-xs font-medium">{getStatusText(facility.status)}</div>
                  <div className="text-xs text-[#F2E2D2]/50 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(facility.lastActivity)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityChart;
